import React, { useEffect, useState } from "react";
import "./HierarchyModal.scss"; // This will contain our new CSS
import { getHierarchy } from "../../services/api/HierarchyApi";
import {
  addMembersManually,
  getBranchByBranchID,
  getBranchDetailsById,
  updateMembersManually,
} from "../../services/api/BranchApi";
import { useLoading } from "../../context/LoadingContext";
import {useToast} from "../../context/ToastContext"

export const HierarchyModal = ({ onClose, branchId, mode = "add" }) => {
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { showLoading, hideLoading } = useLoading();
  const { showToast } = useToast();

  // State to manage expanded/collapsed nodes
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        showLoading();
        const [hierarchyRes, branchRes] = await Promise.all([
          getHierarchy(),
          mode === "edit"
            ? getBranchDetailsById(branchId)
            : Promise.resolve(null),
        ]);

        const usersList = hierarchyRes.data.users || [];
        setUsers(usersList);

        
        const parentIds = new Set(
          usersList
            .map((user) => user.createdBy)
            .filter(Boolean) 
        );
        setExpandedNodes(parentIds);

        if (mode === "edit" && branchRes?.data?.result?.branchData?.user) {
          const existingUserIds = new Set(
            branchRes.data.result.branchData.user
          );
          setSelectedIds(existingUserIds);
          // Optionally, expand all selected nodes' ancestors in edit mode
          //expandAncestors(usersList, existingUserIds);
        }
      } catch (error) {
        console.error("Error:", error);
        showToast("Failed to load data", "error");
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [branchId, mode]);

  const buildTree = (list) => {
    const map = {};
    const roots = [];

    list.forEach((user) => {
      map[user._id] = { ...user, children: [] };
    });

    list.forEach((user) => {
      if (user.createdBy && map[user.createdBy]) {
        map[user.createdBy].children.push(map[user._id]);
      } else {
        roots.push(map[user._id]);
      }
    });

    // Sort children by name for consistent display
    Object.values(map).forEach((node) => {
      node.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return roots.sort((a, b) => a.name.localeCompare(b.name));
  };

  const tree = buildTree(users);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const toggleExpand = (id) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleAddMembers = async () => {
    const usersId = Array.from(selectedIds);
    if (!usersId.length) {
      showToast("Please select at least one member", "error");
      return;
    }

    try {
      showLoading();
      let payload = {};

      if (mode === "edit") {
        const res = await getBranchByBranchID(branchId);
        const branchName = res?.data?.result?.branchData?.branchName;
        payload = { branchName, user: usersId };
        await updateMembersManually(branchId, payload);
        showToast("Members updated successfully", "success");
      } else {
        payload = { branchId, usersId };
        await addMembersManually(payload);
        showToast("Members added successfully", "success");
      }
       onClose();
    } catch (error) {
      console.error("Error updating/adding members:", error);
      showToast("Something went wrong", "error");
    } finally {
      hideLoading();
    }
  };

  const renderTree = (nodes, level = 0) =>
    nodes.map((node) => {
      const isExpanded = expandedNodes.has(node._id);
      const hasChildren = node.children.length > 0;

      return (
        <div
          key={node._id}
          className={`tree-node ${hasChildren ? "has-children" : ""}`}
        >
          <div className="node-content">
            {hasChildren && (
              <span
                className={`expand-icon ${isExpanded ? "expanded" : ""}`}
                onClick={() => toggleExpand(node._id)}
              >
                {/* SVG for right/down arrow */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            )}
            <label className="node-label">
              <input
                type="checkbox"
                checked={selectedIds.has(node._id)}
                onChange={() => toggleSelect(node._id)}
              />
              <span className="node-name">{node.name}</span>
              <span className="node-role">({node.role})</span>
            </label>
          </div>
          {hasChildren && isExpanded && (
            <div className="node-children">
              {renderTree(node.children, level + 1)}
            </div>
          )}
        </div>
      );
    });

  return (
    <div className="hier-modal-overlay" onClick={onClose}>
      <div
        className="hier-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hier-modal-title"
      >
        <h2 id="hier-modal-title">
          {mode === "edit" ? "Update Members" : "Add Members"}
        </h2>
        <div className="hier-modal-content">{renderTree(tree)}</div>

        <div className="hier-modal-actions">
          <button className="hier-modal-submit-btn" onClick={handleAddMembers}>
            {mode === "edit" ? "Update Members" : "Add Members"}
          </button>
          <button className="hier-modal-close-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};